import {
  maybe,
  Maybe,
  mt,
  MutationData,
  rd,
  RemoteData
} from '@passionware/monads';
import { createSimpleStore } from '@passionware/simple-store';
import { useMemo, useState } from 'react';
import useLast from 'use-last';
import { AppScopeProvider } from 'v5/core/app-scope/_private/AppScopeProvider';
import { useChannelMessage } from '../../../platform/messaging/useChannelMessage';
import { WithServices } from '../../../platform/ts/services';
import { WithFeatureService } from '../../../services/front/FeatureService/FeatureService';
import { WithAppScopeService } from '../../../services/internal/AppScopeService/AppScopeService';
import { WithMessageService } from '../../../services/internal/MessageService/MessageService';
import {
  CustomFieldConfigDialog,
  CustomFieldConfigDialogProps
} from './CustomFieldConfigDialog';
import { FieldDefinition } from './field-definition';

export function CustomFieldConfigDialogWidget(
  props: WithServices<
    [WithMessageService, WithFeatureService, WithAppScopeService]
  >
) {
  const initialFieldStore = useMemo(
    () => createSimpleStore<RemoteData<Maybe<FieldDefinition>>>(rd.ofIdle()),
    []
  );
  const channel = useChannelMessage(
    props.services.messageService.createCustomField.subscribeToRequest,
    {
      onConnect: channel => {
        initialFieldStore.setNewValue(
          rd.of(channel.type === 'create' ? null : channel.customField)
        );
      },
      onMessage: async (message, channel) => {
        setMutation(message);
        if (mt.isSuccess(message)) {
          channel.close();
        }
      }
    }
  );

  const [mutation, setMutation] = useState<MutationData<unknown, unknown>>(
    mt.ofIdle()
  );

  const handleSubmit = (data: Maybe<FieldDefinition>) => {
    channel?.channel.emit(maybe.getOrThrow(data));
    setMutation(mt.ofPending(void 0));
  };

  // Keep last request through close animation so AppScopeProvider + flags stay stable.
  const lastChannel = useLast(channel, maybe.isPresent(channel));
  if (!lastChannel) {
    return null;
  }

  return (
    <AppScopeProvider scope={lastChannel.initialPayload.appScope}>
      <CustomFieldConfigDialogWithFlags
        services={props.services}
        open={maybe.isPresent(channel)}
        mode={lastChannel.initialPayload.type}
        onSubmit={handleSubmit}
        mutation={mutation}
        prompt={lastChannel.initialPayload.prompt}
        onCancel={() => channel?.channel.close()}
        resetStore={initialFieldStore}
        disabledFields={
          lastChannel.initialPayload.type === 'edit' ? ['type'] : undefined
        }
      />
    </AppScopeProvider>
  );
}

function CustomFieldConfigDialogWithFlags(
  props: Omit<CustomFieldConfigDialogProps, 'features'> &
    WithServices<[WithFeatureService, WithAppScopeService]>
) {
  const { services, ...dialogProps } = props;
  // Provider above is the opener's appScope from the request — not the URL.
  const featureFlags = services.featureService.useFeatureFlags(
    services.appScopeService.useResolvedScopeIdentity()
  );
  return rd.tryMap(featureFlags, flags => (
    <CustomFieldConfigDialog {...dialogProps} features={flags} />
  ));
}
